import { FormHelperText, Grid, TextField, Tooltip, Box } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateValidationInfo, getRangeDate, toInputFormat } from '../../services';
import { gridLayout } from '../SectionLayout';
import { OptionalControl } from '..';
import type { ICustomMoment, IDateControlProps, IValidityInfo } from '../../../types';

/**
 * Renders an input box for dates.
 * Used for variables which allow the user to pick a date
 * @param {IDateControlProps} props the properties for the date input component
 * @returns {JSX.Element} the date input component
 */
export const DateControl = ({
  featureFlags,
  value,
  variable,
  onChange,
  onOptionalChange,
  disabled,
}: IDateControlProps) => {
  const { t } = useTranslation();
  const [internalDateValue, setInternalDateValue] = useState(value ? dayjs(value) : null);
  const [valueIsUpdated, setValueIsUpdated] = useState(false);
  const date = getSelectedDate(valueIsUpdated, internalDateValue, value);
  const validity = getDateValidationInfo(variable, date);
  const onEventChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const newValue = inputValue ? dayjs(inputValue) : null;
    setInternalDateValue(newValue);
    valueIsUpdated && setValueIsUpdated(false);

    if (!getDateValidationInfo(variable, newValue).valid) {
      //if not valid do not update configuration
      return;
    }

    if (newValue && !newValue.isValid()) {
      return;
    }

    onChange?.(newValue ? newValue.toISOString() : '');
    setValueIsUpdated(true);
  };

  const minDate = getRangeDate(variable, 'lower');
  const maxDate = getRangeDate(variable, 'upper');
  const title = disabled ? t('tooltip.readOnlyTooltipText') : '';

  return (
    <Grid container sx={{ alignItems: 'stretch', rowGap: 0.5 }} size={gridLayout.fullWidth}>
      <Grid
        sx={{ justifyContent: 'left', alignItems: 'center' }}
        size={{
          xs: gridLayout.xs.input,
          sm: gridLayout.sm.input,
        }}
      >
        <Tooltip title={title}>
          <Box style={{ display: 'block', width: '100%' }}>
            <TextField
              type="date"
              value={date?.isValid() ? date.format('YYYY-MM-DD') : ''}
              onChange={onEventChange}
              slotProps={{
                htmlInput: {
                  min: minDate?.isValid() ? minDate.format('YYYY-MM-DD') : undefined,
                  max: maxDate?.isValid() ? maxDate.format('YYYY-MM-DD') : undefined,
                },
              }}
              size="small"
              fullWidth
              disabled={disabled}
              sx={disabled ? { pointerEvents: 'none' } : {}}
            />
          </Box>
        </Tooltip>
      </Grid>
      <Grid
        container
        sx={{ alignItems: 'center', justifyContent: 'center' }}
        size={{
          xs: gridLayout.xs.infoIcon,
          sm: gridLayout.sm.infoIcon,
        }}
      />
      <Grid
        size={{
          xs: gridLayout.xs.qty,
          sm: gridLayout.sm.qty,
        }}
      ></Grid>
      <Grid
        container
        sx={{ alignItems: 'center', justifyContent: 'center' }}
        size={{
          xs: gridLayout.xs.optional,
          sm: gridLayout.sm.optional,
        }}
      >
        <OptionalControl
          featureFamily={variable}
          onOptionalChange={onOptionalChange}
          featureFlag={featureFlags.Optional}
          disabled={disabled}
        />
      </Grid>
      {renderValidityMessage(date as ICustomMoment, validity)}
    </Grid>
  );
};

const getSelectedDate = (
  valueIsUpdated: boolean,
  internalDateValue: Dayjs | null,
  value: string
) => {
  if (!valueIsUpdated) {
    return internalDateValue;
  }

  return value ? dayjs(value) : null;
};

const renderValidityMessage = (dateCopy: ICustomMoment | null, validity: IValidityInfo) => {
  if (!dateCopy || (dateCopy.isValid() && validity.valid)) {
    return null;
  }

  const dateValue = dateCopy._i || dateCopy.format('MM/DD/YYYY');
  return (
    <FormHelperText className="color-red">
      {dateCopy.isValid()
        ? `${toInputFormat(dateValue)} is invalid. Valid values are: ${validity.validValues.join(', ')}`
        : `${dateValue} is invalid. The accepted format is MM/DD/YYYY`}
    </FormHelperText>
  );
};
